import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { AlertComponent } from '../../shared/alert/alert.component';
import { FolderSummaryDto } from '../../shared/models/folder-summary.dto';
import { MergeBeneficiarySearchDto } from '../../shared/models/merge-beneficiairy-search.dto';
import { MergeService } from '../../shared/services/merge.service';
import { ToastService } from '../../shared/toast/toast.service';
import * as CustomValidators from './no-match.validator';

@Component({
  selector: 'app-admin-merge',
  templateUrl: './merge.component.html',
  styleUrls: ['./merge.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MergeComponent implements OnInit, OnDestroy {
  public mergeForm: FormGroup<{
    sourceId: FormControl<MergeBeneficiarySearchDto | null>;
    destId: FormControl<MergeBeneficiarySearchDto | null>;
  }>;
  public submitted: boolean = false;

  public sourceSummary: FolderSummaryDto = null;
  public destSummary: FolderSummaryDto = null;
  private readonly destroy$: Subject<void> = new Subject();
  constructor(
    private readonly mergeService: MergeService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
  ) {}

  public ngOnInit(): void {
    this.mergeForm = new FormGroup({
      sourceId: new FormControl<MergeBeneficiarySearchDto | null>(null, Validators.required),
      destId: new FormControl<MergeBeneficiarySearchDto | null>(null, Validators.required),
    });
    this.mergeForm.addValidators([CustomValidators.noMatchValidator('sourceId', 'destId')]);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public search = (text$: Observable<string>) =>
    text$.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => (term.length < 2 ? of([]) : this.mergeService.search(term))),
    );

  public formatter = (item: MergeBeneficiarySearchDto) => (item ? item.id + ' - ' + item.lastName + ' ' + item.firstName : '');

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (this.mergeForm.valid && this.sourceSummary?.rsj !== 1) {
      const { id: doublonId } = this.mergeForm.controls.sourceId.value;
      const { id: beneficiaryId } = this.mergeForm.controls.destId.value;
      try {
        await this.mergeService.merge({ doublonId, beneficiaryId });
        this.toastService.show("L'opération de fusion s'est déroulée avec succès.", 'success');
      } catch (error) {
        if (error instanceof HttpErrorResponse) {
          let operationName: string;
          switch (error.status) {
            case 444:
              operationName = 'IDs Iodas et Abc';
              break;
            case 445:
              operationName = 'contacts';
              break;
            case 446:
              operationName = 'contrats';
              break;
            case 447:
              operationName = 'étapes';
              break;
            case 448:
              operationName = 'questionnaires FSE';
              break;
            case 449:
              operationName = 'diagnostics IER';
              break;
            case 450:
              operationName = 'traces de passage';
              break;
            case 451:
              operationName = 'informations emploi';
              break;
            case 452:
              operationName = 'documents';
              break;
            case 453:
              this.toastService.show('Le bénéficiaire source ne doit pas posséder de dossier RSJ.', 'danger');
              break;
            case 454:
              operationName = 'diagnostics';
              break;
            default:
              break;
          }
          if (operationName) {
            const message: string = `Une erreur s'est produite, pendant l'opération de fusion des « ${operationName} ». Veuillez corriger ou réessayer ultérieurement.`;
            this.openAlertModal(message);
          }
        }
      }
    }
  }

  public onClosed(controlName: string): void {
    if (this.mergeForm.controls[controlName]) {
      this.mergeForm.controls[controlName].reset('');
      switch (controlName) {
        case 'sourceId':
          this.sourceSummary = null;
          break;
        case 'destId':
          this.destSummary = null;
          break;
        default:
          break;
      }
    }
  }

  public onReinit(): void {
    this.mergeForm.reset({ sourceId: null, destId: null });
    this.submitted = false;
    this.sourceSummary = null;
    this.destSummary = null;
  }

  public onSelectItem(event: NgbTypeaheadSelectItemEvent, controlName: string): void {
    if (event && event.item) {
      this.mergeService
        .getFolderSummary(event.item.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: FolderSummaryDto) => {
            switch (controlName) {
              case 'sourceId':
                this.sourceSummary = res;
                break;
              case 'destId':
                this.destSummary = res;
                break;
              default:
                break;
            }
          },
        });
    }
  }

  private async openAlertModal(message): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(AlertComponent, {
      backdrop: 'static',
      centered: true,
      size: 'lg',
    });
    const modalComponent: AlertComponent = modal.componentInstance as AlertComponent;
    return modalComponent.alert(message);
  }
}
